import {
	getList,
	patchWithoutId,
	getSingle,
	isApiError,
	post,
} from './utilities/provider';
import type { AxiosRequestConfig } from 'axios';
import type { ApiV1 } from '@teamworksdev/influencer/core';
import { UserAxleProfile } from '@/types/teamworks';

const RESOURCE = 'users';

export function useUserService(config?: Omit<AxiosRequestConfig, 'key'>) {
	const getUser = async (bypassImpersonation: boolean = false) => {
		const response = await getList<ApiV1.UserResponse>(`${RESOURCE}/me`, {
			key: 'getUser',
			requiresInflcrId: false,
			requiresAcceptedTou: false,
			bypassImpersonation,
			...config,
		});
		if (isApiError(response)) throw response;
		return response.data.data;
	};

	const updateUser = async (data: ApiV1.UpdateUserRequest) => {
		const response = await patchWithoutId<ApiV1.UserResponse>(`${RESOURCE}/me`, data, {
			key: 'updateUser',
			requiresInflcrId: false,
			...config,
		});
		if (isApiError(response)) throw response;
		return response.data.data;
	};

	const getUserAxleProfile = async () => {
		const response = await getSingle<ApiV1.AppSwitcherUserProfile>(`${RESOURCE}/app-switcher`, '', {
			key: 'getUserAppSwitcherUserProfile',
			...config,
		});
		if (isApiError(response)) throw response;
		return new UserAxleProfile(response.data);
	};

	const getUserConfirmation = async (confirmationCode: string) => {
		const response = await getSingle<ApiV1.UserConfirmationCodeResponse>(`${RESOURCE}/confirm`, confirmationCode, {
			key: 'getUserConfirmation',
			requiresAuth: false,
			requiresInflcrId: false,
			requiresAcceptedTou: false,
			...config,
		});
		if (isApiError(response)) throw response;
		return response.data;
	};

	const confirmUser = async (confirmationCode: string, model: ApiV1.ConfirmUserRequest) => {
		const response = await post<ApiV1.AuthToken>(`${RESOURCE}/confirm/${confirmationCode}`, model, <AxiosRequestConfig>{
			key: 'confirmUser',
			requiresAuth: false,
			requiresInflcrId: false,
			requiresAcceptedTou: false,
			...config,
		});
		if (isApiError(response)) throw response;
		return response.data;
	};

	const updateDefaultProfile = async (profileUuid: string) => {
		const response = await post(`${RESOURCE}/user-profiles/${profileUuid}/default`, {}, {
			key: 'updateDefaultProfile',
			requiresInflcrId: false,
			...config,
		});
		if (isApiError(response)) throw response;
		return response.status;
	};

	return {
		getUser,
		updateUser,
		getUserAxleProfile,
		getUserConfirmation,
		confirmUser,
		updateDefaultProfile,
	};
}
